import VideoPlayer from "@dana/Components/VideoPlayer";
import PrevArrowIcon from "@dana/Icons/PrevArrowIcon";
import Youtube from "@dana/MobiusOverrides/Icons/Youtube";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { EZIDAttributeFilter } from "@dana/Types/EZIDAttributeFilter";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { css } from "styled-components";

interface DetermineInstalledLengthScreenProps {
    video?: LinkFieldValue;
    caption?: LinkFieldValue;
    backLabel?: string;
    slideTitle?: string;
    subText?: string;
    description?: string;
    additionalText?: string;
    image?: string;
    imageAltText?: string;
    watchVideoLabel?: string;
    stepLabel?: string;
    step?: number;
    determineInstalledLength1stOptionLabel?: string;
    determineInstalledLength2ndOptionLabel?: string;
    determineInstalledLength3rdOptionLabel?: string;
    onProceedToNextScreen: (newAttributeFilters?: EZIDAttributeFilter[], subStep?: number) => void;
    onProceedToPreviousScreen: (newAttributeFilters?: EZIDAttributeFilter[]) => void;
}

export interface DetermineInstalledLengthScreenStyles {
    wrapper?: InjectableCss;
    backButton?: ButtonPresentationProps;
    heading?: TypographyPresentationProps;
    subText?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    actionWrapper?: InjectableCss;
    watchButtonWrapper?: InjectableCss;
    image?: ImgProps;
    videoModal?: ModalPresentationProps;
    watchVideoButton?: ButtonPresentationProps;
}

export const styles: DetermineInstalledLengthScreenStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 90%;
            gap: 24px;
            margin: 18px auto 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                        width: 100%;
                                    `,
                        css`
                                        width: 100%;
                                    `,
                        css`
                                        width: 100%;
                                    `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    backButton: {
        variant: "tertiary",
        css: css`
            align-self: flex-start;
            text-align: left;
        `,
        icon: {
            src: PrevArrowIcon,
            position: "left",
        },
    },
    heading: {
        variant: "h3",
        css: css`
            margin-bottom: 0;
            text-align: center;
        `,
    },
    subText: {
        variant: "h4",
        css: css`
            text-align: center;
            margin-bottom: 0;
        `,
    },
    description: {
        variant: "h5",
        transform: "none",
        color: themeColors.primary.richBlack,
        css: css`
                        letter-spacing: inherit;
                        text-align: center;
                        margin-bottom: 0;
                    `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            border: 1px solid ${themeBackgrounds.lightGray};
            border-radius: 4px;
            padding: 16px;
            box-sizing: border-box;

            & > *:not(:last-child) {
                border-bottom: 1px solid ${themeBackgrounds.lightGray};
                padding-bottom: 16px;
                margin-bottom: 16px;
            }
        `,
    },
    image: {
        css: css`
        width: 100%;
        height: auto;
        max-height: 480px;
        object-fit: contain;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-height: 180px;
                        `,
                        css`
                            max-height: 220px;
                        `,
                        css`
                            max-height: 260px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
    `,
    },
    videoModal: {
        cssOverrides: {
            modalContent: css`
            video {
                width: 100%;
                height: auto;
                max-height: 70vh;
                display: block;
            }
        `,
        },
        sizeVariant: "large",
    },
    actionWrapper: {
        css: css`
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                width: 100%;
                gap: 16px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                            flex-direction: column;
                                            align-items: stretch;
                                            gap: 12px;
                                        `,
                        css`
                                            flex-direction: column;
                                            align-items: stretch;
                                            gap: 14px;
                                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
            `,
    },
    watchButtonWrapper: {
        css: css`
                border-bottom: none !important;
            `,
    },
    watchVideoButton: {
        variant: "primary",
        icon: {
            position: "left",
            src: () => <Youtube />
        },
    },
};

const DetermineInstalledLengthScreen: React.FC<DetermineInstalledLengthScreenProps> = ({
    video,
    caption,
    backLabel,
    slideTitle,
    subText,
    description,
    additionalText,
    image,
    imageAltText,
    watchVideoLabel,
    stepLabel,
    step,
    determineInstalledLength1stOptionLabel,
    determineInstalledLength2ndOptionLabel,
    determineInstalledLength3rdOptionLabel,
    onProceedToNextScreen,
    onProceedToPreviousScreen,
}) => {
    const [videoModalIsOpen, setVideoModalIsOpen] = useState(false);
    const videoLink = video ? useGetLink(video) : null;
    const captionLink = caption ? useGetLink(caption) : null;

    const onButtonClick = (subStep: number) => {
        onProceedToNextScreen(undefined, subStep);
    }

    const onBackButtonClick = () => {
        onProceedToPreviousScreen(undefined);
    }

    const videoModalCloseHandler = () => {
        setVideoModalIsOpen(false);
    };

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Button
                    {...styles.backButton}
                    onClick={onBackButtonClick}>
                    {backLabel}
                </Button>
                <Typography {...styles.heading} as="h3">
                    {slideTitle}
                </Typography>
                <Typography {...styles.subText} as="h4">
                    {subText}
                </Typography>
                <Typography {...styles.description} as="h5">
                    {description}
                </Typography>
                {additionalText && (
                    <Typography {...styles.subText} as="h4">
                        {additionalText}
                    </Typography>
                )}
                <StyledWrapper {...styles.contentWrapper}>
                    {image && (
                        <Img src={image} altText={imageAltText || ''} {...styles.image} />
                    )}
                    <StyledWrapper {...styles.actionWrapper}>
                        <Button variant="primary" onClick={() => onButtonClick(1)}>
                            {determineInstalledLength1stOptionLabel}
                        </Button>
                        <Button variant="primary" onClick={() => onButtonClick(2)}>
                            {determineInstalledLength2ndOptionLabel}
                        </Button>
                        <Button variant="primary" onClick={() => onButtonClick(3)}>
                            {determineInstalledLength3rdOptionLabel}
                        </Button>
                    </StyledWrapper>
                    {videoLink?.url && (
                        <StyledWrapper {...styles.watchButtonWrapper}>
                            <Button {...styles.watchVideoButton} onClick={() => setVideoModalIsOpen(true)}>
                                {watchVideoLabel}
                            </Button>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.actionWrapper}>
                        <Typography as="b">{stepLabel} {step}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <Modal
                {...styles.videoModal}
                id="determineInstalledLengthVideoModal"
                isOpen={videoModalIsOpen}
                handleClose={videoModalCloseHandler}>
                <StyledWrapper>
                    <VideoPlayer
                        videoUrl={videoLink?.url || ''}
                        videoCaptionUrl={captionLink?.url || ''}
                    />
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    )
};

export default DetermineInstalledLengthScreen;